import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Chip,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  transition: theme.transitions.create(['transform', 'box-shadow']),
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[3],
  },
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

const LanguageChip = styled(Chip)(({ theme, language }) => {
  // Color mapping for different languages
  const colorMap = {
    en: theme.palette.info.main,
    es: theme.palette.error.main,
    fr: theme.palette.primary.main,
    de: theme.palette.warning.dark,
    it: theme.palette.success.main,
    pt: theme.palette.secondary.main,
    ja: theme.palette.error.light,
    zh: theme.palette.warning.main,
    // Add more languages as needed
  };

  return {
    backgroundColor: colorMap[language] || theme.palette.grey[500],
    color: '#fff',
    fontWeight: 500,
  };
});

const TranscriptionText = styled(Typography)(({ theme }) => ({
  lineHeight: 1.6,
  fontWeight: 400,
}));

const TimeStamp = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
}));

/**
 * Styled transcription card component
 * @param {Object} props Component props
 * @param {string} props.text Transcription text
 * @param {string} props.language Language code
 * @param {string} props.timestamp Timestamp of the transcription
 * @param {string} props.sender Sender of the transcription
 * @param {boolean} props.showSender Whether to show the sender information
 * @returns {JSX.Element} TranscriptionCard component
 */
const TranscriptionCard = ({ 
  text, 
  language = 'en', 
  timestamp = new Date().toLocaleTimeString(), 
  sender = 'Unknown',
  showSender = false,
  languageNames = {
    en: 'English',
    es: 'Spanish',
    fr: 'French',
    de: 'German',
    it: 'Italian',
    pt: 'Portuguese',
    ja: 'Japanese',
    zh: 'Chinese',
  }
}) => {
  return (
    <StyledCard elevation={1}>
      <CardContent>
        <CardHeader>
          <LanguageChip 
            language={language}
            label={languageNames[language] || language} 
            size="small" 
          />
          <TimeStamp variant="caption">{timestamp}</TimeStamp>
        </CardHeader>
        
        <Divider sx={{ my: 1 }} />
        
        <TranscriptionText variant="body1">
          {text}
        </TranscriptionText>
        
        {showSender && (
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Typography variant="caption" color="text.secondary">
              From: {sender}
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default TranscriptionCard;
